import { getRequestGeneric, putRequestGeneric } from '@/services/backendApiGeneric'
import { gt } from '@/i18n/gt'
import { parseNumberRanges } from '@/utils/caseSelection'

// Vertretung / Abwesenheit (Kundenfeedback Kanzlei 08.09.2026, I): Wie eine
// Abwesenheitsregel in Outlook. Während des Zeitraums erhält die Vertretung
// alle Benachrichtigungen und Zuweisungen des Kontos zusätzlich und sieht
// dessen Akten. Backend: internal/tenants/substitution.

/**
 * Fallauswahl je Regel (Kundenfeedback Kanzlei 13.09.2026, I): Teilen sich
 * mehrere Vertretungen die Akten einer Kollegin auf, übernimmt z. B. die
 * eine nur die ungeraden, die andere nur die geraden Fallnummern; daneben
 * sind eigene Nummernbereiche möglich („1-50, 77, 120-“).
 */
export type SubstitutionCaseScope = 'all' | 'odd' | 'even' | 'custom'

export interface SubstitutionEntry {
  id: number
  idUser: number
  idSubstitute: number
  substituteName: string
  userName: string
  validFrom: string // YYYY-MM-DD
  validUntil: string | null // YYYY-MM-DD, null = bis auf Weiteres
  note: string
  isActive: boolean
  caseScope: SubstitutionCaseScope
  caseScopeRule: string
}

/**
 * Abwesenheitsnotiz mit automatischer Antwort (Kundenfeedback Kanzlei
 * 18.10.2026, V): Schreibt ein Externer während der Abwesenheit, antwortet
 * KARS einmal automatisch – ohne Zeitraum oder Rückkehrdatum.
 * Backend: internal/tenants/absencenotice.
 */
export interface AbsenceNoticeSettings {
  enabled: boolean
  message: string
  /** Vorschlagstext des Servers (nur lesend). */
  defaultMessage?: string
}

export const ABSENCE_NOTICE_MAX_LENGTH = 1000

export interface SubstitutionOverview {
  idUser: number
  /** Regeln des Nutzers: „wird vertreten von …“ */
  entries: SubstitutionEntry[]
  /** Regeln, in denen der Nutzer selbst Vertreter ist: „vertritt …“ */
  representing: SubstitutionEntry[]
  absenceNotice?: AbsenceNoticeSettings
}

export interface SubstitutionCandidate {
  id: number
  name: string
  initials: string
  email: string
  userRole: string
}

/** Zu speichernde Regel (Teilmenge von SubstitutionEntry). */
export interface SubstitutionDraft {
  idSubstitute: number
  validFrom: string
  validUntil: string | null
  note: string
  caseScope: SubstitutionCaseScope
  caseScopeRule: string
}

/** Auswahlwerte für die Fallauswahl im Editor. */
export const SUBSTITUTION_CASE_SCOPES: Array<{ value: SubstitutionCaseScope, label: string }> = [
  { value: 'all', label: 'Alle Fallakten' },
  { value: 'odd', label: 'Nur ungerade Fallnummern' },
  { value: 'even', label: 'Nur gerade Fallnummern' },
  { value: 'custom', label: 'Eigene Nummernbereiche …' },
]

/** Kurzbeschreibung der Fallauswahl („nur ungerade Fallnummern“); leer bei „alle“. */
export function describeSubstitutionScope (scope: SubstitutionCaseScope | string | null | undefined, rule: string | null | undefined): string {
  const mode = (scope || 'all') as SubstitutionCaseScope
  switch (mode) {
    case 'odd': {
      return gt('nur ungerade Fallnummern')
    }
    case 'even': {
      return gt('nur gerade Fallnummern')
    }
    case 'custom': {
      const range = (rule || '').trim()
      return range ? `${gt('Fallnummern')} ${range}` : gt('eigene Nummernauswahl')
    }
    default: {
      return ''
    }
  }
}

/** Prüft, ob eine eigene Nummernauswahl mindestens einen gültigen Bereich enthält. */
export function isValidSubstitutionScopeRule (rule: string | null | undefined): boolean {
  return parseNumberRanges(rule || '').length > 0
}

function basePath (userId: number | null): string {
  return userId ? `/protected/users/${userId}/substitutions` : '/protected/users/me/substitutions'
}

export function getSubstitutions (userId: number | null = null): Promise<SubstitutionOverview> {
  return getRequestGeneric(basePath(userId))
}

export function saveSubstitutions (
  entries: SubstitutionDraft[],
  userId: number | null = null,
  absenceNotice: AbsenceNoticeSettings | null = null,
): Promise<SubstitutionOverview> {
  const body = absenceNotice
    ? { entries, absenceNotice: { enabled: absenceNotice.enabled, message: absenceNotice.message } }
    : { entries }
  return putRequestGeneric(basePath(userId), body, true)
}

/**
 * Findet Datums- oder Zeitraumangaben im Text der Abwesenheitsnotiz – die
 * gehen Externe nichts an (Kundenwunsch). Liefert den ersten Fund oder ''.
 */
export function findPeriodHint (text: string | null | undefined): string {
  const t = text || ''
  const patterns = [
    /\b\d{1,2}\.\s?\d{1,2}\.(\s?\d{2,4})?/,
    /\b\d{4}-\d{2}-\d{2}\b/,
    /\b(bis|ab)\s+(zum|einschließlich|voraussichtlich|montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag|\d)/i,
    /\b(zurück am|wieder im haus|wieder erreichbar|rückkehr|urlaub|krank)/i,
  ]
  for (const re of patterns) {
    const m = t.match(re)
    if (m) {
      return m[0].trim()
    }
  }
  return ''
}

export function getSubstitutionCandidates (userId: number | null = null): Promise<SubstitutionCandidate[]> {
  const query = userId ? `?idUser=${userId}` : ''
  return getRequestGeneric(`/protected/users/me/substitutions/candidates${query}`)
}

/** Formatiert YYYY-MM-DD als DD.MM.YYYY; leer für null. */
export function formatSubstitutionDate (iso: string | null | undefined): string {
  if (!iso) {
    return ''
  }
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) {
    return iso
  }
  return `${d}.${m}.${y}`
}
